// Phase 4D.3A — browser-native voice controls. No paid STT/TTS service.
//
// Explicit user gesture only. No auto-recording, no background listening, the
// mic state is always visible, and voice never confirms an action.

import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import {
  detectVoiceSupport,
  micStateLabel,
  sanitizeForSpeech,
  type MicState,
  type VoiceSupport,
} from "@/lib/intelligence/assistant/voice";

interface Props {
  /** Called with the recognised transcript. The composer stays in control. */
  onTranscript: (text: string) => void;
  /** Latest sanitised assistant text available for playback. */
  speakText: string | null;
  disabled?: boolean;
}

export function VoiceControls({ onTranscript, speakText, disabled }: Props) {
  const [support, setSupport] = useState<VoiceSupport | null>(null);
  const [mic, setMic] = useState<MicState>("idle");
  const [speaking, setSpeaking] = useState(false);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    setSupport(detectVoiceSupport(typeof window === "undefined" ? null : (window as never)));
    return () => {
      try {
        recognitionRef.current?.stop?.();
        window.speechSynthesis?.cancel?.();
      } catch {
        /* nothing to clean up */
      }
    };
  }, []);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop?.();
    } catch {
      /* already stopped */
    }
    recognitionRef.current = null;
    setMic("idle");
  }, []);

  const startListening = useCallback(() => {
    if (!support?.recognition) {
      setMic("unsupported");
      return;
    }
    const w = window as unknown as { SpeechRecognition?: any; webkitSpeechRecognition?: any };
    const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Ctor) {
      setMic("unsupported");
      return;
    }
    try {
      const rec = new Ctor();
      rec.lang = "en-US";
      rec.interimResults = false;
      rec.continuous = false;
      rec.onresult = (e: any) => {
        const text = String(e?.results?.[0]?.[0]?.transcript ?? "").trim();
        if (text) onTranscript(text);
      };
      rec.onerror = (e: any) => {
        setMic(e?.error === "not-allowed" || e?.error === "service-not-allowed" ? "denied" : "error");
        recognitionRef.current = null;
      };
      rec.onend = () => {
        recognitionRef.current = null;
        setMic((s) => (s === "listening" ? "idle" : s));
      };
      recognitionRef.current = rec;
      setMic("listening");
      rec.start();
    } catch {
      setMic("error");
    }
  }, [onTranscript, support]);

  const toggleSpeak = useCallback(() => {
    if (!support?.synthesis || !speakText) return;
    const synth = window.speechSynthesis;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const utter = new SpeechSynthesisUtterance(sanitizeForSpeech(speakText));
    utter.onend = () => setSpeaking(false);
    utter.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(utter);
  }, [speakText, speaking, support]);

  if (!support) return null;

  return (
    <div className="flex items-center gap-1">
      {support.recognition ? (
        <button
          type="button"
          onClick={mic === "listening" ? stopListening : startListening}
          disabled={disabled}
          aria-pressed={mic === "listening"}
          aria-label={micStateLabel(mic)}
          title={micStateLabel(mic)}
          className={`rounded-xl border p-2 transition disabled:opacity-40 ${
            mic === "listening"
              ? "animate-pulse border-gold/70 text-gold"
              : "border-border hover:border-gold/60"
          }`}
        >
          {mic === "listening" ? (
            <Mic className="h-4 w-4" aria-hidden="true" />
          ) : (
            <MicOff className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      ) : null}

      {support.synthesis ? (
        <button
          type="button"
          onClick={toggleSpeak}
          disabled={disabled || !speakText}
          aria-pressed={speaking}
          aria-label={speaking ? "Stop reading the answer aloud" : "Read the answer aloud"}
          title={speaking ? "Stop reading aloud" : "Read aloud"}
          className="rounded-xl border border-border p-2 transition hover:border-gold/60 disabled:opacity-40"
        >
          {speaking ? (
            <VolumeX className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Volume2 className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      ) : null}

      <span className="sr-only" role="status" aria-live="polite">
        {micStateLabel(mic)}
      </span>
      {support.fallbackMessage ? (
        <span className="ml-1 hidden text-[11px] text-muted-foreground sm:inline">
          {support.fallbackMessage}
        </span>
      ) : null}
    </div>
  );
}
